// One campaign's report (UI-7): the campaign as the marketer made it — every
// country side by side, the totals, the A/B arms, the opens and clicks over
// time, the revenue in every attribution window — each number naming its
// source. Reached from a report card (a group), from the country table or
// from the campaign's own page.

import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { DownloadIcon, SendIcon } from "lucide-react";
import {
  ApiError,
  downloadReportCsv,
  fetchCampaignReport,
  fetchCampaignTags,
  fetchTagDimensions,
  saveCampaignTags,
} from "@/lib/api";
import type { CampaignReport as CountryDetailReport, CampaignTags } from "@/lib/api";
import { fetchCampaignGroupReport, fetchGroupReport } from "@/lib/tracking";
import type { GroupCountryReport, GroupReport } from "@/lib/tracking";
import { useLoad } from "@/lib/useLoad";
import { routeId } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Curve } from "@/components/emc/charts/Curve";
import { DataTable } from "@/components/emc/DataTable";
import { DetailList } from "@/components/emc/DetailList";
import { Field } from "@/components/emc/Field";
import { PageHeader } from "@/components/emc/PageHeader";
import { RuleRate } from "@/components/emc/RuleRate";
import { SourceMark } from "@/components/emc/SourceMark";
import { StatGrid, StatTile } from "@/components/emc/StatTile";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, ErrorState, LoadingState } from "@/components/emc/states";
import { count, minute } from "@/components/emc/format";
import { labelOf } from "@/components/emc/status";
import type { Tone } from "@/components/emc/status";
import { POOL_MIN_SAMPLE, judge, rateText } from "@/components/emc/thresholds";
import { usePageCrumb } from "@/components/emc/shell/crumb";
import { armRows, euro, figures, fxNote, permilleText, timelineSeries, warehouseNote, windows } from "@/pages/reports/model";
import type { ArmRow } from "@/pages/reports/model";
import { cn } from "@/lib/utils";
import { Country } from "@/components/emc/Flag";

function messageOf(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) return error.message;
  return "Ismeretlen hiba.";
}

const TONE_TEXT: Record<Tone, string> = {
  running: "text-blue-ink",
  scheduled: "text-indigo-ink",
  good: "text-green-ink",
  warn: "text-orange-ink font-semibold",
  danger: "text-red-ink font-semibold",
  muted: "",
};

// --- the countries ---------------------------------------------------------------------

const COUNTRY_COLUMNS: ColumnDef<GroupCountryReport>[] = [
  { id: "country", header: "Ország", cell: ({ row }) => <Country code={row.original.country} /> },
  {
    id: "state",
    header: "Állapot",
    cell: ({ row }) => <StatusPill domain="campaign" state={row.original.state} />,
  },
  { id: "sent", header: "Küldve", cell: ({ row }) => count(row.original.postgres.sent), meta: { align: "right" } },
  {
    id: "delivered",
    header: "Kézbesítve",
    cell: ({ row }) => rateText(row.original.postgres.delivered, row.original.postgres.sent),
    meta: { align: "right" },
  },
  {
    id: "bounce",
    header: "Bounce",
    cell: ({ row }) => (
      <RuleRate
        verdict={judge("bounce", row.original.postgres.bounced, row.original.postgres.sent, POOL_MIN_SAMPLE)}
        minSample={POOL_MIN_SAMPLE}
      />
    ),
    meta: { align: "right" },
  },
  {
    id: "complaint",
    header: "Panasz",
    cell: ({ row }) => (
      <RuleRate
        verdict={judge("complaint", row.original.postgres.complained, row.original.postgres.sent, POOL_MIN_SAMPLE)}
        minSample={POOL_MIN_SAMPLE}
      />
    ),
    meta: { align: "right" },
  },
  {
    id: "unsubscribed",
    header: "Leiratkozás",
    cell: ({ row }) => rateText(row.original.postgres.unsubscribed, row.original.postgres.sent),
    meta: { align: "right" },
  },
  {
    id: "opens",
    header: "Egyedi nyitás",
    cell: ({ row }) =>
      row.original.clickhouse === null ? (
        <span className="text-muted-foreground">nem elérhető</span>
      ) : (
        count(row.original.clickhouse.opens_total)
      ),
    meta: { align: "right" },
  },
  {
    id: "ctr",
    header: "CTR",
    cell: ({ row }) =>
      row.original.clickhouse === null ? (
        <span className="text-muted-foreground">nem elérhető</span>
      ) : (
        permilleText(row.original.clickhouse.ctr_permille)
      ),
    meta: { align: "right" },
  },
  { id: "orders", header: "Rendelés", cell: ({ row }) => count(row.original.postgres.orders), meta: { align: "right" } },
  {
    id: "revenue",
    header: "Bevétel",
    cell: ({ row }) => (
      <span title={fxNote(row.original.postgres.fx_first_day, row.original.postgres.fx_last_day) || undefined}>
        {euro(row.original.postgres.revenue_eur_cents)}
      </span>
    ),
    meta: { align: "right" },
  },
];

/** One country opened under its row: the provider split, the links, the
 * assembly, the tags — the 5.3 country report, as it was. */
function CountryDetail({ campaignId }: { campaignId: number }) {
  const [detail] = useLoad<CountryDetailReport>(() => fetchCampaignReport(campaignId), [campaignId]);
  if (detail.error !== null) return <ErrorState message={detail.error} />;
  if (detail.data === null) return <LoadingState lines={3} />;
  const report = detail.data;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="flex min-w-0 flex-col gap-2">
        <h3 className="flex items-baseline gap-2">
          Levelezőnként <SourceMark source="Postgres" />
        </h3>
        <DataTable
          label="Levelezőnként"
          variant="plain"
          className="rounded-card border border-line bg-surface"
          data={report.by_isp.filter((row) => row.sent + row.delivered + row.bounced + row.complained > 0)}
          getRowId={(row) => row.isp}
          columns={[
            { id: "isp", header: "Levelező", cell: ({ row }) => labelOf("isp", row.original.isp) },
            { id: "sent", header: "Küldve", cell: ({ row }) => count(row.original.sent), meta: { align: "right" } },
            {
              id: "delivered",
              header: "Kézbesítve",
              cell: ({ row }) => rateText(row.original.delivered, row.original.sent),
              meta: { align: "right" },
            },
            {
              id: "bounced",
              header: "Bounce",
              cell: ({ row }) => rateText(row.original.bounced, row.original.sent),
              meta: { align: "right" },
            },
            {
              id: "complained",
              header: "Panasz",
              cell: ({ row }) => rateText(row.original.complained, row.original.sent),
              meta: { align: "right" },
            },
          ]}
        />
      </section>
      <section className="flex min-w-0 flex-col gap-2">
        <h3 className="flex items-baseline gap-2">
          Linkek <SourceMark source="ClickHouse" />
        </h3>
        {report.links === null ? (
          <p className="m-0 text-muted-foreground">nem elérhető — az eseménytárház nem válaszolt</p>
        ) : report.links.length === 0 ? (
          <p className="m-0 text-muted-foreground">Még senki nem kattintott.</p>
        ) : (
          <DataTable
            label="Linkek"
            variant="plain"
            className="rounded-card border border-line bg-surface"
            data={report.links}
            getRowId={(link) => String(link.link_no)}
            columns={[
              { id: "link", header: "Link", cell: ({ row }) => row.original.link_no, meta: { mono: true } },
              {
                id: "total",
                header: "Kattintás",
                cell: ({ row }) => count(row.original.total_clicks),
                meta: { align: "right" },
              },
              {
                id: "unique",
                header: "Egyedi",
                cell: ({ row }) => count(row.original.unique_clicks),
                meta: { align: "right" },
              },
              {
                id: "human",
                header: "Emberi",
                cell: ({ row }) => count(row.original.unique_human_clicks),
                meta: { align: "right" },
              },
            ]}
          />
        )}
        <h3 className="mt-2 flex items-baseline gap-2">
          Összeállítás <SourceMark source="Postgres" />
        </h3>
        {report.assembly === null ? (
          <p className="m-0 text-muted-foreground">Erről a kampányról nincs összeállítási esemény.</p>
        ) : (
          <DetailList
            items={[
              { label: "jelölt", value: count(report.assembly.candidates ?? null) },
              { label: "kizárás miatt kimaradt", value: count(report.assembly.suppressed ?? null) },
              { label: "gyakorisági korlát", value: count(report.assembly.frequency_capped ?? null) },
              { label: "kizáró szegmens", value: count(report.assembly.excluded_by_segment ?? null) },
              { label: "másik országból kapja", value: count(report.assembly.group_duplicate ?? null) },
              { label: "címzett lett", value: count(report.assembly.recipients_written ?? null) },
            ]}
          />
        )}
      </section>
      <TagEditor campaignId={campaignId} className="lg:col-span-2" />
    </div>
  );
}

function TagEditor({ campaignId, className }: { campaignId: number; className?: string }) {
  const [dimensions] = useLoad(fetchTagDimensions, []);
  const [current] = useLoad<CampaignTags>(() => fetchCampaignTags(campaignId), [campaignId]);
  const [tags, setTags] = useState<CampaignTags | null>(null);
  const [state, setState] = useState<{ busy: boolean; error: string | null; saved: boolean }>({
    busy: false,
    error: null,
    saved: false,
  });
  useEffect(() => {
    if (current.data !== null) setTags(current.data);
  }, [current.data]);

  if (dimensions.data === null || tags === null) return null;

  async function save() {
    if (tags === null) return;
    setState({ busy: true, error: null, saved: false });
    try {
      setTags(await saveCampaignTags(campaignId, tags));
      setState({ busy: false, error: null, saved: true });
    } catch (cause: unknown) {
      setState({ busy: false, error: messageOf(cause), saved: false });
    }
  }

  return (
    <section className={cn("flex flex-col gap-3", className)}>
      <h3>Tagek (ennek az országnak a kampánya)</h3>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {dimensions.data.map((dimension) => (
          <Field key={dimension.key} label={dimension.label} htmlFor={`tag-${campaignId}-${dimension.key}`}>
            <Input
              id={`tag-${campaignId}-${dimension.key}`}
              type="text"
              value={(tags[dimension.key] ?? []).join(", ")}
              onChange={(event) =>
                setTags({
                  ...tags,
                  [dimension.key]: event.target.value
                    .split(",")
                    .map((part) => part.trim())
                    .filter((part) => part !== ""),
                })
              }
            />
          </Field>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" disabled={state.busy} onClick={() => void save()}>
          {state.busy ? "Mentés…" : "Tagek mentése"}
        </Button>
        <span className="text-label text-muted-foreground">
          {state.saved
            ? "A tagek elmentve."
            : "Vesszővel elválasztva több érték is megadható; a mentés a kampány teljes tag-halmazát cseréli."}
        </span>
      </div>
      {state.error !== null && <ErrorState message={state.error} />}
    </section>
  );
}

// --- the arms -----------------------------------------------------------------------------

const ARM_COLUMNS: ColumnDef<ArmRow>[] = [
  {
    id: "name",
    header: "Változat · tárgy",
    cell: ({ row }) => (
      <span className="flex min-w-56 flex-col gap-0.5">
        <span className="flex items-center gap-2">
          <span className="font-medium">{row.original.name}</span>
          {row.original.winner && <StatusPill tone="good">nyertes</StatusPill>}
          {!row.original.winner && row.original.wonIn !== "" && (
            <StatusPill tone="good">nyertes: {row.original.wonIn}</StatusPill>
          )}
        </span>
        <span className="text-label text-muted-foreground">{row.original.subject || "—"}</span>
      </span>
    ),
    meta: { wrap: true },
  },
  { id: "share", header: "Arány", cell: ({ row }) => row.original.share, meta: { align: "right", muted: true } },
  { id: "sent", header: "Küldve", cell: ({ row }) => row.original.sent, meta: { align: "right" } },
  { id: "delivered", header: "Kézbesítve", cell: ({ row }) => row.original.delivered, meta: { align: "right" } },
  {
    id: "opens",
    header: "Emberi nyitás",
    cell: ({ row }) => row.original.opens,
    meta: { align: "right" },
  },
  {
    id: "clicks",
    header: "Emberi kattintás",
    cell: ({ row }) => row.original.clicks,
    meta: { align: "right" },
  },
  {
    id: "bounce",
    header: "Bounce",
    cell: ({ row }) => <span className={TONE_TEXT[row.original.bounceTone]}>{row.original.bounce}</span>,
    meta: { align: "right" },
  },
  {
    id: "complaint",
    header: "Panasz",
    cell: ({ row }) => <span className={TONE_TEXT[row.original.complaintTone]}>{row.original.complaint}</span>,
    meta: { align: "right" },
  },
  { id: "unsub", header: "Leiratkozás", cell: ({ row }) => row.original.unsubscribed, meta: { align: "right" } },
  {
    id: "revenue",
    header: "Bevétel",
    cell: ({ row }) => (
      <span className="flex flex-col items-end">
        <span>{row.original.revenue}</span>
        <span className="text-label text-muted-foreground">{row.original.orders} rendelés</span>
      </span>
    ),
    meta: { align: "right" },
  },
];

// --- the page ------------------------------------------------------------------------------

function Report({ report }: { report: GroupReport }) {
  const [open, setOpen] = useState<number | null>(null);
  const [csvError, setCsvError] = useState<string | null>(null);
  usePageCrumb(report.name);
  const headline = figures(report.totals.postgres, report.totals.clickhouse, report.primary_window);
  const note = warehouseNote(report);
  const series = useMemo(() => (report.timeline ? timelineSeries(report.timeline) : null), [report.timeline]);
  const byWindow = windows(report.totals.postgres, report.primary_window);
  const countries = report.countries.map((row) => row.country);

  function onCsv() {
    setCsvError(null);
    const [path, name] =
      report.group_id !== null
        ? [`/reports/groups/${report.group_id}.csv`, `kampany-csoport-${report.group_id}.csv`]
        : [`/reports/campaigns/${report.campaign_id}.csv`, `kampany-${report.campaign_id}.csv`];
    downloadReportCsv(path, name).catch((cause: unknown) => setCsvError(messageOf(cause)));
  }

  return (
    <>
      <PageHeader
        back={{ to: "/riportok", label: "Riportok" }}
        title={report.name}
        meta={<StatusPill domain={report.group_id !== null ? "campaignGroup" : "campaign"} state={report.state} />}
        description={
          <>
            <span className="data-mono">{countries.join(" ")}</span>
            {report.mode ? ` · ${labelOf("campaignMode", report.mode)}` : ""}
            {report.started_at ? ` · indult ${minute(report.started_at)}` : " · még nem indult"}
            {report.finished_at ? ` · kész ${minute(report.finished_at)}` : ""}
            {Object.keys(report.tags).length > 0 && (
              <>
                {" · "}
                {Object.entries(report.tags)
                  .map(([key, values]) => `${key}: ${values.join(", ")}`)
                  .join(" · ")}
              </>
            )}
          </>
        }
        actions={
          <>
            {report.group_id !== null && (
              <Button variant="outline" asChild>
                <Link to={`/kampanyok/${report.group_id}`}>
                  <SendIcon />
                  Kampány
                </Link>
              </Button>
            )}
            <Button variant="outline" type="button" onClick={onCsv}>
              <DownloadIcon />
              CSV
            </Button>
          </>
        }
      />

      <div className="flex flex-col gap-6">
        {csvError !== null && <ErrorState message={csvError} />}
        {note !== null && (
          <Callout tone="warn">
            {note.text}
            {note.reason && (
              <span className="mt-1 block truncate text-label text-muted-foreground" title={note.reason}>
                {note.reason}
              </span>
            )}
          </Callout>
        )}

        <StatGrid>
          {headline.map((figure) => (
            <StatTile
              key={figure.key}
              label={figure.label}
              value={
                figure.available ? (
                  figure.value
                ) : (
                  <span className="text-body font-normal text-muted-foreground">{figure.value}</span>
                )
              }
              comparison={
                figure.detail ? (
                  <>
                    {figure.comparison}
                    <span className="block">{figure.detail}</span>
                  </>
                ) : (
                  figure.comparison
                )
              }
              tone={figure.tone}
              trend={<SourceMark source={figure.source} />}
            />
          ))}
        </StatGrid>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-baseline gap-2">
              Nyitás és kattintás időben <SourceMark source="ClickHouse" />
            </CardTitle>
            <CardDescription>
              Egyedi nyitás és kattintás óránként, az első eseménytől 72 óráig, minden ország együtt,
              a te órád szerint.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {series === null ? (
              <p className="m-0 text-muted-foreground">
                nem elérhető — az eseménytárház nem adott idősort, ezért nincs görbe.
              </p>
            ) : series.opens.length === 0 ? (
              <p className="m-0 text-muted-foreground">Még nincs nyitás.</p>
            ) : (
              <div className="grid gap-6 lg:grid-cols-2">
                <div className="flex min-w-0 flex-col gap-2">
                  <h3>Egyedi nyitás óránként</h3>
                  <Curve data={series.opens} ariaLabel="Egyedi nyitás óránként" actualLabel="nyitás" />
                </div>
                <div className="flex min-w-0 flex-col gap-2">
                  <h3>Egyedi kattintás óránként</h3>
                  <Curve data={series.clicks} ariaLabel="Egyedi kattintás óránként" actualLabel="kattintás" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Országonként</CardTitle>
            <CardDescription>
              A bounce és a panasz a 4. szabály határaihoz mérve ({POOL_MIN_SAMPLE} küldéstől). A sor
              kinyitva a levelezőnkénti bontást, a linkeket, az összeállítást és az ország tagjeit mutatja.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="m-0 flex flex-wrap items-center gap-x-3 text-label text-muted-foreground">
              <span>
                <SourceMark source="Postgres" />: küldve, kézbesítve, bounce, panasz, leiratkozás, rendelés, bevétel
              </span>
              <span>
                <SourceMark source="ClickHouse" />: egyedi nyitás, CTR
              </span>
            </p>
            <DataTable
              label="Országonként"
              variant="plain"
              className="-mx-5.5 border-t border-separator"
              columns={COUNTRY_COLUMNS}
              data={report.countries}
              getRowId={(row) => String(row.campaign_id)}
              onRowClick={(row) => setOpen(open === row.campaign_id ? null : row.campaign_id)}
              isRowSelected={(row) => row.campaign_id === open}
              renderExpanded={(row) => (open === row.campaign_id ? <CountryDetail campaignId={row.campaign_id} /> : null)}
            />
          </CardContent>
        </Card>

        {report.ab !== null && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-baseline gap-2">
                A/B teszt <SourceMark source="Postgres" />
              </CardTitle>
              <CardDescription>
                A tesztcsoport változatonként, és a maradék, amely a döntés után a nyertest kapta. A
                nyitás és a kattintás itt a Postgres eseménynaplójából jön, egy ember egyszer, a gépi
                nyitások nélkül — ugyanazok a számok, amelyekből a döntés született
                {report.ab.metric ? ` (${labelOf("abMetric", report.ab.metric)} szerint)` : ""}.
                {report.ab.decided_at ? ` Döntés: ${minute(report.ab.decided_at)}.` : " Még nincs döntés."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <DataTable
                label="A/B teszt"
                variant="plain"
                className="-mx-5.5 border-t border-separator"
                columns={ARM_COLUMNS}
                data={armRows(report.ab.variants)}
                getRowId={(row) => row.key}
                rowClassName={(row) => (row.key === "rest" ? "text-muted-foreground" : undefined)}
              />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-baseline gap-2">
              Bevétel attribúciós ablakonként <SourceMark source="Postgres" />
            </CardTitle>
            <CardDescription>
              Ugyanazok a rendelések, minden ablakon át; a fejléc a {labelOf("attributionWindow", report.primary_window)}{" "}
              számát mutatja, mert ezzel mérhető össze a Klaviyo-riport. Minden összeg EUR-ban, rendelésenként
              a rendelés napjának EKB-referenciaárfolyamán; ami a napja árfolyamára még vár, az nincs benne.
              {fxNote(report.totals.postgres.fx_first_day, report.totals.postgres.fx_last_day) &&
                ` ${fxNote(report.totals.postgres.fx_first_day, report.totals.postgres.fx_last_day)}.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {byWindow.length === 0 ? (
              <p className="m-0 text-muted-foreground">Ehhez a kampányhoz még nem számolt el rendelést egy ablak sem.</p>
            ) : (
              <DetailList
                columns={2}
                items={byWindow.flatMap((row) => [
                  { label: row.label, value: row.fx ? `${row.revenue} · ${row.fx}` : row.revenue },
                  { label: "rendelés", value: row.orders },
                ])}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

/** `/riportok/csoport/:id` reads a group; `/riportok/kampany/:id` a country
 * campaign, which the API answers with its group's report when it has one. */
export default function CampaignReportPage({ by }: { by: "group" | "campaign" }) {
  const params = useParams();
  const id = routeId(params.id);
  const [report, reload] = useLoad<GroupReport>(
    () => (id === null ? Promise.reject(new Error("Nincs ilyen kampány.")) : by === "group" ? fetchGroupReport(id) : fetchCampaignGroupReport(id)),
    [id, by],
  );
  if (id === null) return <Navigate to="/riportok" replace />;
  if (report.data === null) {
    return (
      <>
        <PageHeader back={{ to: "/riportok", label: "Riportok" }} title="Kampány-riport" />
        {report.error !== null ? <ErrorState message={report.error} onRetry={reload} /> : <LoadingState lines={6} />}
      </>
    );
  }
  return <Report report={report.data} />;
}
